import { useEffect, useRef, useState, type ReactNode, type TouchEvent } from "react";
import { useRoleSwitch } from "../auth/useRoleSwitch";
import { RoleMenu } from "./RoleMenu";
import css from "./MarqueeHeader.module.css";

/**
 * Single-row HUD header used by every role (OO style): one plaque holding
 * `{ROLE} · {ник} · UniBooking` with an auto-marquee when it overflows, plus
 * the DateSelector on the right. Colour follows the cascading --role token.
 * Tapping the plaque (multi-role users) opens the role-switch dropdown.
 */
export function MarqueeHeader({
  roleLabel,
  nick,
  dateSelector,
}: {
  roleLabel: string;
  nick: string;
  dateSelector?: ReactNode;
}) {
  const innerRef = useRef<HTMLDivElement>(null);
  const marqRef = useRef<HTMLDivElement>(null);
  const [anim, setAnim] = useState(false);
  const { canSwitch, cycle } = useRoleSwitch();
  const [menuOpen, setMenuOpen] = useState(false);

  // Horizontal swipe ON THIS HEADER ROW → previous/next role. Scoped to the bar
  // so it never fires on the scrollable content below. Threshold ≥56px and
  // clearly horizontal so a tap or a date-chevron press never triggers it.
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const onHeaderTouchStart = (e: TouchEvent) => {
    const t = e.touches[0];
    swipeStart.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const onHeaderTouchEnd = (e: TouchEvent) => {
    const s = swipeStart.current;
    swipeStart.current = null;
    if (!s || !canSwitch) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) > 56 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      cycle(dx < 0 ? 1 : -1); // swipe left → next role, swipe right → previous
    }
  };

  useEffect(() => {
    const inner = innerRef.current;
    const wrap = marqRef.current;
    if (!inner || !wrap) return;
    const overflow = inner.scrollWidth - wrap.clientWidth;
    if (overflow > 4) {
      inner.style.setProperty("--mq-shift", `-${overflow}px`);
      inner.style.setProperty("--mq-dur", `${Math.max(14, overflow / 14)}s`);
      setAnim(true);
    } else {
      setAnim(false);
    }
  }, [roleLabel, nick]);

  return (
    <header
      className={css.appHead}
      onTouchStart={canSwitch ? onHeaderTouchStart : undefined}
      onTouchEnd={canSwitch ? onHeaderTouchEnd : undefined}
    >
      <div className={css.roleAnchor}>
        <div
          className={`${css.marqueeBadge} ${canSwitch ? css.clickable : ""}`}
          role={canSwitch ? "button" : undefined}
          tabIndex={canSwitch ? 0 : undefined}
          aria-haspopup={canSwitch ? "menu" : undefined}
          aria-expanded={canSwitch ? menuOpen : undefined}
          onClick={canSwitch ? () => setMenuOpen((o) => !o) : undefined}
        >
          <span className={`${css.rbCorner} ${css.rbTl}`} aria-hidden="true" />
          <span className={`${css.rbCorner} ${css.rbBr}`} aria-hidden="true" />
          <div className={css.marquee} ref={marqRef}>
            <div className={`${css.marqueeInner} ${anim ? css.anim : ""}`} ref={innerRef}>
              <span className={css.rbText}>{roleLabel}</span>
              <span className={css.mqSep}>·</span>
              <span className={css.rbUser}>{nick}</span>
              <span className={css.mqSep}>·</span>
              <span className={css.wm}>UniBooking</span>
            </div>
          </div>
          {canSwitch ? <span className={css.roleChev} aria-hidden="true">▾</span> : null}
        </div>
        {canSwitch ? <RoleMenu open={menuOpen} onClose={() => setMenuOpen(false)} /> : null}
      </div>
      {dateSelector ? <div className={css.headDate} data-hdr-date="">{dateSelector}</div> : null}   {/* 11.10: окна (проверка допработ) кладут сюда свой календарь вместо дневного */}
    </header>
  );
}
